import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
    Drawer,
    DrawerOverlay,
    Button,
    Text,
    DrawerContent,
    DrawerBody,
    DrawerHeader,
    DrawerFooter,
    useStyleConfig,
    useBreakpointValue,
    Box,
    Flex,
    Divider,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import Refinements from './refinements'
import SortByAccordionItem from './sort-by-accordion'
import PropTypes from 'prop-types'
import {Close16Icon} from '../../../components/icons'
import LoadingSpinner from '../../../components/loading-spinner'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'

export default function ProductFilterDrawer({
    searchParams,
    productSearchResult,
    selectedSortingOptionLabel,
    sortUrls,
    isOpen,
    onClose,
    filtersLoading,
    toggleFilter,
    resetFilters,
}) {
    const refinements = productSearchResult?.refinements ?? []
    const refine = searchParams?.refine ?? {}
    const hasSelectedFilters = Object.keys(refine).length > 0
    const styles = useStyleConfig('ProductFilterDrawer')
    const drawerPlacement = useBreakpointValue({base: 'bottom', md: 'right'})

    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const exludedFilterslist = !notShoppableCountry
        ? ['cgid', 'c_isPDPOnlyPurchasable']
        : ['cgid', 'price', 'c_isPDPOnlyPurchasable'] || []

    const bodyRef = useRef(null)
    const [lastSelectedFilter, setLastSelectedFilter] = useState(null)

    /**
     * Freeze the first valid refinements array HERE — in ProductFilterDrawer,
     * which is always mounted at page level — not inside Refinements or the
     * Drawer content, which can unmount when the drawer closes.
     *
     * Timeline:
     *  1. Page loads  → refinements = []           → no freeze yet (empty guard)
     *  2. First API response → refinements = [all] → freeze
     *  3. User filters → refinements = [partial]   → ref already set, ignored
     *  4. Drawer opens → Refinements mounts         → receives pre-merged data
     */
    const frozenRefinements = useRef(null)

    if (refinements.length > 0 && !frozenRefinements.current) {
        frozenRefinements.current = refinements
    }

    /**
     * Build a live hit count lookup from the CURRENT API response:
     *   { [attributeId]: { [value]: hitCount } }
     *
     * Values absent from the current response (filtered out by the API
     * because they have zero matches) will not appear here and will
     * default to 0 during the merge below.
     */
    const currentHitCountMap = {}
    for (const filter of refinements) {
        currentHitCountMap[filter.attributeId] = {}
        for (const val of filter.values || []) {
            currentHitCountMap[filter.attributeId][val.value] = val.hitCount ?? 0
        }
    }

    /**
     * Merge: frozen structure (which filters/values to show) +
     *        live hit counts (current count, 0 if API omitted the value).
     *
     * Refinements receives this merged array so it never has to worry
     * about values disappearing — it just renders what it gets.
     */
    const activeRefinements = (frozenRefinements.current || refinements).map((filter) => ({
        ...filter,
        values: (filter.values || []).map((val) => ({
            ...val,
            hitCount: currentHitCountMap[filter.attributeId]?.[val.value] ?? 0,
        })),
    }))

    const handleToggleFilter = useCallback(
        (value, attributeId, ...rest) => {
            setLastSelectedFilter(`${attributeId}:${value?.value}`)
            toggleFilter?.(value, attributeId, ...rest)
        },
        [toggleFilter],
    )

    useEffect(() => {
        if (!lastSelectedFilter || filtersLoading) return

        const frame = window.requestAnimationFrame(() => {
            const container = bodyRef.current
            if (!container) return

            const escape = window.CSS?.escape ?? ((str) => str.replace(/([^\w-])/g, '\\$1'))
            const element = container.querySelector(
                `[data-refinement-value="${escape(lastSelectedFilter)}"]`,
            )
            if (!element) return

            const offset =
                element.getBoundingClientRect().top - container.getBoundingClientRect().top
            container.scrollTop += offset - (container.clientHeight - element.offsetHeight) / 2
        })

        return () => window.cancelAnimationFrame(frame)
    }, [lastSelectedFilter, filtersLoading, productSearchResult])

    useEffect(() => {
        if (!isOpen) {
            setLastSelectedFilter(null)
        }
    }, [isOpen])

    return (
        <Drawer
            variant="filter"
            placement={drawerPlacement}
            onClose={onClose}
            isOpen={isOpen}
            background="background.neutral.default"
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader {...styles?.header}>
                    <Flex {...styles?.headerContent}>
                        <Text {...styles?.headerText}>
                            <FormattedMessage
                                defaultMessage="Filter and Sort"
                                id="product_list.modal.title.filter"
                            />
                        </Text>
                        <Button
                            type="button"
                            aria-label="Close filters"
                            variant="ghost"
                            onClick={onClose}
                            {...styles?.closeButton}
                        >
                            <Box {...styles?.closeIcon}>
                                <Close16Icon vectorSize={9.5} />
                            </Box>
                        </Button>
                    </Flex>
                    <Divider {...styles?.headerDivider} />
                </DrawerHeader>

                <DrawerBody {...styles.body} ref={bodyRef}>
                    {filtersLoading && <LoadingSpinner />}
                    <Refinements
                        width="100%"
                        toggleFilter={handleToggleFilter}
                        filters={activeRefinements}
                        selectedFilters={refine}
                        isOpen={isOpen}
                        itemsBefore={[
                            <>
                                <SortByAccordionItem
                                    width="100%"
                                    key="sortby"
                                    selectedSortingOptionLabel={selectedSortingOptionLabel}
                                    sortUrls={sortUrls}
                                    productSearchResult={productSearchResult}
                                />
                                <Box {...styles?.border} />
                            </>,
                        ]}
                        excludedFilters={exludedFilterslist}
                    />
                </DrawerBody>

                <DrawerFooter {...styles?.footer}>
                    <Flex {...styles?.footerInnerContainer}>
                        <Button
                            onClick={resetFilters}
                            size={{base: 'xl', sm: 'xl', md: 'large', lg: 'large'}}
                            variant="ghost-not-rounded"
                            isDisabled={!hasSelectedFilters}
                        >
                            <FormattedMessage
                                defaultMessage="Remove filters"
                                id="product_list.modal.button.remove_filters"
                            />
                        </Button>
                        <Button
                            size={{base: 'xl', sm: 'xl', md: 'large', lg: 'large'}}
                            variant="primary-not-rounded"
                            onClick={onClose}
                            isDisabled={!hasSelectedFilters}
                        >
                            <FormattedMessage
                                defaultMessage="Apply filters"
                                id="product_list.modal.button.apply_filters"
                            />
                            {hasSelectedFilters && <Text>({productSearchResult?.total})</Text>}
                        </Button>
                    </Flex>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

ProductFilterDrawer.propTypes = {
    searchParams: PropTypes.object,
    productSearchResult: PropTypes.object,
    selectedSortingOptionLabel: PropTypes.object,
    sortUrls: PropTypes.array,
    category: PropTypes.object,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filtersLoading: PropTypes.bool,
    toggleFilter: PropTypes.func,
    resetFilters: PropTypes.func,
}
